import React, { useContext, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import PaidIcon from '@mui/icons-material/Paid';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import GroupIcon from '@mui/icons-material/Group';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ApartmentIcon from '@mui/icons-material/Apartment';
import InsightsIcon from '@mui/icons-material/Insights';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { AuthContext } from '../context/auth-context';

const TEAL = '#009db9';
const TEAL_DARK = '#00707f';

const menuItems = [
  { text: 'Dashboard', path: '/dashboard' },
  { text: 'Financial Dashboard', path: '/financial-dashboard', roles: ['FINANCIAL_ADMIN'] },
  { text: 'Manager Dashboard', path: '/manager-dashboard', roles: ['MANAGER'] },
  { text: 'Vacation Management', path: '/vacation-management' },
  { text: 'Earnings Management', path: '/earnings-management' },
  { text: 'Bonus Management', path: '/bonus-management', roles: ['FINANCIAL_ADMIN'] },
  { text: 'User Management', path: '/user-management' },
  { text: 'Bank Management', path: '/bank-management' },
  { text: 'Department Management', path: '/department-management' },
  { text: 'Manager Team Metrics', path: '/manager-team-metrics' },
  { text: 'OTP Management', path: '/otp-management' },
];

// Presentational only — maps a path to an icon. Does not affect routing,
// filtering, or anything in menuItems above.
const ICONS = {
  '/dashboard': DashboardIcon,
  '/financial-dashboard': AccountBalanceIcon,
  '/manager-dashboard': SupervisorAccountIcon,
  '/vacation-management': BeachAccessIcon,
  '/earnings-management': PaidIcon,
  '/bonus-management': CardGiftcardIcon,
  '/user-management': GroupIcon,
  '/bank-management': AccountBalanceWalletIcon,
  '/department-management': ApartmentIcon,
  '/manager-team-metrics': InsightsIcon,
  '/otp-management': VpnKeyIcon,
};

const EXPANDED_WIDTH = 250;
const COLLAPSED_WIDTH = 84;

const Sidebar = ({ onItemClick }) => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const filteredMenuItems = user?.role === 'FINANCIAL_ADMIN'
    ? menuItems.filter(item =>
        ['/financial-dashboard', '/earnings-management', '/bonus-management', '/bank-management'].includes(item.path)
      )
    : user?.role === 'MANAGER'
    ? menuItems.filter(item =>
        ['/manager-dashboard', '/vacation-management', '/manager-team-metrics'].includes(item.path)
      )
    : menuItems.filter(item => !item.roles || item.roles.includes(user?.role));

  return (
    <Box
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        position: 'sticky',
        top: { xs: '80px', md: '100px' },
        height: 'calc(100vh - 100px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: '#fff',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '20px',
        px: collapsed ? 1 : 1.5,
        py: 2,
        flexShrink: 0,
        transition: 'width 0.2s ease, padding 0.2s ease',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', mb: 1.5 }}>
        <IconButton
          onClick={() => setCollapsed((c) => !c)}
          size="small"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          sx={{
            bgcolor: 'rgba(0,157,185,0.1)',
            color: TEAL_DARK,
            '&:hover': { bgcolor: 'rgba(0,157,185,0.18)' },
          }}
        >
          {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
        </IconButton>
      </Box>

      <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 0 }}>
        {filteredMenuItems.map((item) => {
          const Icon = ICONS[item.path] || DashboardIcon;
          const isActive = location.pathname === item.path;

          const listItem = (
            <ListItem
              button
              component={RouterLink}
              to={item.path}
              onClick={onItemClick}
              sx={{
                py: 1.3,
                px: collapsed ? 1.3 : 1.75,
                borderRadius: '12px',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
                justifyContent: collapsed ? 'center' : 'flex-start',
                position: 'relative',
                backgroundColor: isActive ? 'rgba(0,157,185,0.1)' : 'transparent',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0,157,185,0.1)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '20%',
                  bottom: '20%',
                  width: '3px',
                  borderRadius: '0 4px 4px 0',
                  backgroundColor: isActive ? TEAL : 'transparent',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, color: isActive ? TEAL_DARK : 'text.secondary' }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: isActive ? 700 : 600,
                    color: isActive ? TEAL_DARK : '#333',
                  }}
                />
              )}
            </ListItem>
          );

          return collapsed ? (
            <Tooltip key={item.text} title={item.text} placement="right" arrow>
              {listItem}
            </Tooltip>
          ) : (
            <React.Fragment key={item.text}>{listItem}</React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default Sidebar;